import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search } from "lucide-react";
import { api, list } from "@/lib/api";
import { ProductCard } from "@/components/site/ProductCard";
import { Reveal, Eyebrow } from "@/components/site/Reveal";

const Chip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`rounded-full border px-4 py-2 text-sm transition-colors duration-300 ${active ? "border-[#a855f7] bg-[#7c3aed] text-white" : "border-[#3b1d63] text-[#cbbbe6] hover:border-[#a855f7]"}`}>
    {children}
  </button>
);

const STATUSES = [["", "All"], ["Available", "Available"], ["Sold", "Sold"]];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Row = ({ label, children }) => (
  <div className="flex flex-wrap items-center gap-2">
    <span className="font-mono-j w-16 shrink-0 text-[11px] uppercase tracking-[0.25em] text-[#a78bce]">{label}</span>
    {children}
  </div>
);

export default function Shop() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ categories: [], brands: [] });
  const [f, setF] = useState({ category: "", status: "", q: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products/meta").then((r) => setMeta({ categories: r.data?.categories ?? [], brands: r.data?.brands ?? [] })).catch(() => {});
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
      api.get("/products", { params }).then((r) => setItems(list(r))).catch(() => setItems([])).finally(() => setLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [f]);

  return (
    <div className="mx-auto max-w-[1400px] px-5 pb-32 pt-32 md:px-10 md:pt-40" data-testid="shop-page">
      <Reveal>
        <Eyebrow>The shop</Eyebrow>
        <h1 className="font-display mt-5 text-6xl leading-[0.9] text-[#f3eefc] md:text-8xl">Every piece, <span className="italic text-[#d8b4fe]">one of one.</span></h1>
      </Reveal>
      <div className="mt-14 flex flex-col gap-5 border-y border-[#281245] py-6">
        <label className="flex w-full items-center gap-3 border-b border-[#3b1d63] pb-2 md:max-w-sm">
          <Search className="h-4 w-4 text-[#a78bce]" />
          <input value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Search items" data-testid="shop-search-input" className="w-full bg-transparent text-[#f3eefc] outline-none placeholder:text-[#7d6a9c]" />
        </label>
        <Row label="Category">
          <Chip active={!f.category} onClick={() => setF({ ...f, category: "" })} testid="filter-category-all">All</Chip>
          {meta.categories.map((c) => <Chip key={c} active={f.category === c} onClick={() => setF({ ...f, category: c })} testid={`filter-category-${slug(c)}`}>{c}</Chip>)}
        </Row>
        <Row label="Status">
          {STATUSES.map(([v, l]) => <Chip key={l} active={f.status === v} onClick={() => setF({ ...f, status: v })} testid={`filter-status-${l.toLowerCase()}`}>{l}</Chip>)}
        </Row>
      </div>
      <p className="font-mono-j mt-8 text-xs uppercase tracking-[0.25em] text-[#a78bce]" data-testid="shop-count">{items.length} pieces</p>
      {!loading && !items.length ? (
        <div className="py-24 text-center" data-testid="shop-empty">
          <p className="font-display text-4xl text-[#f3eefc]">Nothing here — yet.</p>
          <Link to="/#request" className="mt-6 inline-block text-[#d8b4fe] underline underline-offset-8">Request it and we'll find it</Link>
        </div>
      ) : (
        <div className="mt-8 grid grid-cols-2 gap-x-4 gap-y-12 md:gap-x-6 lg:grid-cols-4">
          {items.map((p, i) => <Reveal key={p.id} delay={(i % 4) * 0.06}><ProductCard p={p} /></Reveal>)}
        </div>
      )}
    </div>
  );
}
